import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  BiBriefcase,
  BiGroup,
  BiFile,
  BiDollar,
  BiTimeFive,
} from "react-icons/bi";

import { API_BASE_URL } from "../utils/urls.js";
import axiosClient from '../utils/apiClient.js';

const StatCard = ({ title, value, icon, link, colorClass = "text-gray-500" }) => {
  return (
    <Link
      to={link}
      className="block bg-white p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-lg transition-transform hover:-translate-y-1"
    >
      <div className="flex items-center space-x-4">
        {React.cloneElement(icon, { className: `w-10 h-10 ${colorClass}` })}
        <div>
          <p className="text-gray-500 text-sm sm:text-base">{title}</p>
          <p className="text-xl font-semibold text-gray-900 break-words">{value}</p>
        </div>
      </div>
    </Link>
  );
};

const CaseManagerDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [activity, setActivity] = useState([]);
  const [error, setError] = useState(null);

  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const fetchStats = async () => {
      const token = localStorage.getItem("token");
      if (!token) return setError("Authentication required.");

      try {
        const res = await axiosClient.get(`/dashboard/admin/stats`);

        setStats(res.data.metrics);
        setActivity(res.data.activityLog);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch dashboard data.");
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading)
    return (
      <div className="flex items-center justify-center h-64">
        <p className="animate-pulse text-gray-600">Loading dashboard...</p>
      </div>
    );

  if (error)
    return (
      <div className="p-6">
        <p className="bg-red-100 border border-red-300 text-red-600 p-4 rounded-lg">
          {error}
        </p>
      </div>
    );

  const formatCurrency = (amt) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
      parseFloat(amt || 0)
    );

  return (
    <div className="p-4 sm:p-6 space-y-8 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-800">
        Welcome back, {user?.firstName || "Case Manager"} 👋
      </h1>

      {/* Stats */}
     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <StatCard title="Clients" value={stats.numberOfClients} icon={<BiGroup />} link="/clients" colorClass="text-blue-500" />
      <StatCard title="Cases" value={stats.numberOfCases} icon={<BiBriefcase />} link="/cases" colorClass="text-purple-500" />
      <StatCard title="Team Members" value={stats.numberOfTeamMembers} icon={<BiGroup />} link="/teams" colorClass="text-green-500" />
      <StatCard title="Unpaid Invoices" value={stats.unpaidInvoicesCount} icon={<BiFile />} link="/invoices" colorClass="text-red-500" />
      <StatCard title="Total Revenue" value={formatCurrency(stats.totalRevenue)} icon={<BiDollar />} link="/invoices" colorClass="text-yellow-500" />
      <StatCard title="Revenue (Last 30 Days)" value={formatCurrency(stats.revenueThisMonth)} icon={<BiTimeFive />} link="/invoices" colorClass="text-indigo-500" />
    </div>

      {/* Activity Log */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Recent Activity</h2>
        <div className="overflow-x-auto">
          <ul className="divide-y divide-gray-200">
            {activity.length > 0 ? (
              activity.map((log) => (
                <li key={log.id} className="py-4 flex flex-col sm:flex-row sm:justify-between sm:items-center">
                  <div>
                    <p className="text-gray-700 font-medium">{log.description}</p>
                    <p className="text-sm text-gray-400">
                      Case: {log.case_number} — {log.case_name}
                    </p>
                  </div>
                  <div className="text-sm text-gray-500 mt-2 sm:mt-0 text-right">
                    {log.user.full_name}
                    <br />
                    <span className="text-xs">
                      {new Date(log.created_at).toLocaleString()}
                    </span>
                  </div>
                </li>
              ))
            ) : (
              <p className="text-gray-400 text-center py-4">No recent activity.</p>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CaseManagerDashboard;
